import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Image, Text, VStack, HStack } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import SEO from './SEO.jsx'
import { trackCTAClick } from '../services/analytics'

const MotionBox = motion.create(Box)
const MotionVStack = motion.create(VStack)

const BANDA_LINES = [
  'Gestiona a fondo tu banda:',
  'repertorios, ensayos, actuaciones, conexión con entidades religiosas, tus métricas y muchas cosas más.',
]

const ENTIDAD_LINES = [
  'Encuentra de forma rápida banda para tu evento o estación de penitencia por todo el país.',
  'Llega a acuerdos y comunícate de forma rápida y eficaz.',
]

const MASCOTS = {
  banda: { src: '/musico-cofradia-1.png', alt: 'Músico de cofradía' },
  entidad: { src: '/capirote2-new.png', alt: 'Capirote' },
}

const ease = [0.22, 1, 0.36, 1]

const GATEWAY_BG =
  'linear-gradient(135deg, #4c1d95 0%, #6d28d9 42%, #7c3aed 70%, #d97706 100%)'

const HomeGateway = () => {
  const navigate = useNavigate()
  const [hoverKey, setHoverKey] = useState(null)

  const lines = hoverKey === 'banda' ? BANDA_LINES : hoverKey === 'entidad' ? ENTIDAD_LINES : null
  const mascot = hoverKey ? MASCOTS[hoverKey] : null

  return (
    <Box
      data-landing="gateway"
      minH="100dvh"
      w="100%"
      position="relative"
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
      py={{ base: 10, md: 16 }}
      bg={GATEWAY_BG}
    >
      <SEO
        description="La plataforma para gestionar y conectar bandas y entidades religiosas."
        path="/"
      />

      <Box
        position="absolute"
        inset={0}
        pointerEvents="none"
        opacity={0.35}
        style={{
          backgroundImage:
            'radial-gradient(ellipse 80% 60% at 20% 20%, rgba(255,255,255,0.18), transparent 55%), radial-gradient(ellipse 70% 50% at 85% 75%, rgba(245,158,11,0.28), transparent 50%)',
        }}
      />

      <HStack
        position="absolute"
        top={{ base: 4, md: 6 }}
        right={{ base: 4, md: 8 }}
        zIndex={2}
        gap={3}
        align="center"
        flexWrap="wrap"
        justify="flex-end"
      >
        <Text
          color="whiteAlpha.900"
          fontSize={{ base: 'sm', md: 'md' }}
          fontWeight="500"
          textShadow="0 1px 8px rgba(0,0,0,0.25)"
        >
          ¿Ya tienes cuenta?
        </Text>
        <Button
          size={{ base: 'sm', md: 'md' }}
          variant="outline"
          borderWidth="2px"
          borderColor="whiteAlpha.800"
          color="white"
          rounded="full"
          bg="transparent"
          fontWeight="700"
          _hover={{ bg: 'whiteAlpha.200', borderColor: 'white', color: 'white' }}
          onClick={() => {
            trackCTAClick('iniciar_sesion', 'home_gateway')
            navigate('/login')
          }}
        >
          Iniciar sesión
        </Button>
      </HStack>

      <MotionVStack
        gap={{ base: 8, md: 10 }}
        textAlign="center"
        maxW="3xl"
        w="full"
        position="relative"
        zIndex={1}
        initial="hidden"
        animate="show"
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.55, delayChildren: 0.15 } },
        }}
      >
        <MotionBox
          variants={{
            hidden: { opacity: 0, y: 18, scale: 0.94 },
            show: {
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { duration: 0.9, ease },
            },
          }}
        >
          <Image
            src="/logo y texto cadencya blanco.png"
            alt="Cadencya"
            h={{ base: '72px', sm: '96px', md: '120px' }}
            w="auto"
            mx="auto"
            mixBlendMode="screen"
            filter="drop-shadow(0 12px 28px rgba(0,0,0,0.35))"
          />
        </MotionBox>

        <MotionBox
          variants={{
            hidden: { opacity: 0, y: 14 },
            show: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.7, ease },
            },
          }}
          w="full"
        >
          <HStack
            gap={{ base: 3, md: 5 }}
            justify="center"
            flexDirection={{ base: 'column', sm: 'row' }}
            w="full"
            maxW="lg"
            mx="auto"
          >
            <Button
              size="lg"
              w={{ base: 'full', sm: 'auto' }}
              minW={{ sm: '220px' }}
              px={8}
              py={7}
              fontSize="md"
              fontWeight="700"
              bg="white"
              color="purple.800"
              rounded="full"
              shadow="lg"
              _hover={{ bg: 'white', transform: 'translateY(-2px)', shadow: 'xl' }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.2s"
              onMouseEnter={() => setHoverKey('banda')}
              onMouseLeave={() => setHoverKey(null)}
              onFocus={() => setHoverKey('banda')}
              onBlur={() => setHoverKey(null)}
              onClick={() => {
                trackCTAClick('soy_banda_gateway', 'home_gateway')
                navigate('/bandas')
              }}
            >
              Soy una banda
            </Button>

            <Button
              size="lg"
              w={{ base: 'full', sm: 'auto' }}
              minW={{ sm: '220px' }}
              px={8}
              py={7}
              fontSize="md"
              fontWeight="700"
              variant="outline"
              borderWidth="2px"
              borderColor="white"
              color="white"
              rounded="full"
              bg="transparent"
              _hover={{
                bg: 'whiteAlpha.200',
                borderColor: 'white',
                transform: 'translateY(-2px)',
                color: 'white',
              }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.2s"
              onMouseEnter={() => setHoverKey('entidad')}
              onMouseLeave={() => setHoverKey(null)}
              onFocus={() => setHoverKey('entidad')}
              onBlur={() => setHoverKey(null)}
              onClick={() => {
                trackCTAClick('soy_entidad_gateway', 'home_gateway')
                navigate('/hermandades')
              }}
            >
              Soy una entidad religiosa
            </Button>
          </HStack>
        </MotionBox>

        <MotionBox
          variants={{
            hidden: { opacity: 0, y: 16 },
            show: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.75, ease },
            },
          }}
          h={{ base: '16rem', sm: '10rem', md: '11rem' }}
          w="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={2}
          overflow="visible"
        >
          <AnimatePresence mode="wait">
            {lines && mascot ? (
              <MotionBox
                key={hoverKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.35, ease }}
                w="full"
                maxW="2xl"
                mx="auto"
              >
                <HStack
                  gap={{ base: 3, md: 5 }}
                  align="center"
                  justify="center"
                  flexDirection={{ base: 'column', sm: 'row' }}
                >
                  <MotionBox
                    flexShrink={0}
                    animate={{ y: [0, -10, 0], rotate: [-3, 3, -3] }}
                    transition={{
                      duration: 2.6,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    <Image
                      src={mascot.src}
                      alt={mascot.alt}
                      h={{ base: '72px', sm: '88px', md: '104px' }}
                      w="auto"
                      filter="drop-shadow(0 8px 18px rgba(0,0,0,0.35))"
                    />
                  </MotionBox>

                  <VStack gap={1} align={{ base: 'center', sm: 'flex-start' }} textAlign={{ base: 'center', sm: 'left' }}>
                    {lines.map((line, i) => (
                      <MotionBox
                        key={`${hoverKey}-${i}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease, delay: 0.08 + i * 0.12 }}
                        w="full"
                      >
                        <Text
                          as={i === 0 ? 'h1' : 'p'}
                          fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                          fontWeight="600"
                          color="white"
                          lineHeight="tall"
                          textShadow="0 2px 18px rgba(0,0,0,0.35)"
                        >
                          {line}
                        </Text>
                      </MotionBox>
                    ))}
                  </VStack>
                </HStack>
              </MotionBox>
            ) : null}
          </AnimatePresence>
        </MotionBox>
      </MotionVStack>
    </Box>
  )
}

export default HomeGateway
